import { useMemo } from "react"
import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"

import { Loader } from "@productlane/ui"

import { api } from "@/lib/api"
import { formatWeekLabel, formatWeekRangeLabel } from "./week-label"

interface ChartPoint {
	bucket: string
	rate: number
	converted: number
	total: number
	weekLabel: string
	weekRange: string
}

interface TooltipProps {
	active?: boolean
	payload?: Array<{ payload: ChartPoint }>
}

function ConversionTooltip({ active, payload }: TooltipProps) {
	if (active && payload && payload.length > 0) {
		const point = payload[0]?.payload
		if (!point) return null
		return (
			<div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
				<p className="mb-1 text-sm font-medium text-gray-900 dark:text-white">
					{point.weekRange}
				</p>
				<p className="text-sm text-gray-600 dark:text-gray-400">
					Conversion: {point.rate}%
				</p>
				<p className="text-sm text-gray-600 dark:text-gray-400">
					{point.converted} of {point.total} trials
				</p>
			</div>
		)
	}
	return null
}

export function ConversionRateChart() {
	const conversionQuery = api.analytics.conversionRateChart.useQuery({})

	const chartData = useMemo<ChartPoint[]>(() => {
		if (!conversionQuery.data) return []
		return conversionQuery.data.map((item) => ({
			...item,
			weekLabel: formatWeekLabel(item.bucket),
			weekRange: formatWeekRangeLabel(item.bucket),
		}))
	}, [conversionQuery.data])

	const hasData = chartData.some((d) => d.total > 0)

	return (
		<div className="rounded-lg border p-4">
			<h3 className="mb-3 text-sm font-medium text-gray-800 dark:text-white">
				Trial-to-paid conversion rate
			</h3>
			<div className="h-64">
				{conversionQuery.isLoading ? (
					<div className="flex h-full items-center justify-center">
						<Loader />
					</div>
				) : hasData ? (
					<div className="h-full w-full [&_*]:outline-none">
						<ResponsiveContainer width="100%" height="100%">
							<LineChart data={chartData} margin={{ left: -10, right: 8 }}>
								<CartesianGrid
									className="stroke-gray-200 dark:stroke-gray-700"
									vertical={false}
								/>
								<XAxis
									dataKey="weekLabel"
									tick={{ fontSize: 10 }}
									angle={-45}
									textAnchor="end"
									height={50}
									tickLine={false}
									axisLine={{
										className: "stroke-gray-300 dark:stroke-gray-700",
									}}
								/>
								<YAxis
									domain={[0, 100]}
									tick={{ fontSize: 10 }}
									axisLine={false}
									tickLine={false}
									tickFormatter={(value: number) => `${value}%`}
								/>
								<Tooltip
									content={<ConversionTooltip />}
									cursor={{ stroke: "rgba(0, 0, 0, 0.1)" }}
								/>
								<Line
									type="monotone"
									dataKey="rate"
									stroke="#4CB782"
									strokeWidth={2}
									dot={false}
									activeDot={{ r: 4 }}
								/>
							</LineChart>
						</ResponsiveContainer>
					</div>
				) : (
					<div className="flex h-full items-center justify-center text-sm text-secondary">
						No conversion data yet
					</div>
				)}
			</div>
		</div>
	)
}
